import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { registerSW } from "virtual:pwa-register";
import { TaskBurstProvider } from "./components/TaskBurst";
import { ToastProvider } from "./components/Toast";
import { App } from "./App";
import { ApiError } from "./lib/api";
import { initNative, isNative } from "./lib/native";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
      staleTime: 5_000,
    },
  },
});

// The family's plan lapsed while the app was open: reload the session so the app shows the plan screen.
window.addEventListener("mr:subscription-required", () => {
  void queryClient.invalidateQueries({ queryKey: ["me"] });
});

// Apply a saved theme preference before first paint (System / Light / Dark).
try {
  const theme = localStorage.getItem("mr.theme");
  if (theme === "light" || theme === "dark") document.documentElement.dataset.theme = theme;
} catch {
  /* storage unavailable */
}

// Back in the foreground the plan may have changed in the store's own screens.
initNative(() => void queryClient.invalidateQueries());

if ("serviceWorker" in navigator && import.meta.env.PROD && !isNative()) {
  registerSW({ immediate: true });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <TaskBurstProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </TaskBurstProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
